import {
  useFetcher,
  useLoaderData,
  useNavigate
} from "@remix-run/react";
import Header from "../components/header";
import {
  getExpiryDate,
  websiteName,
  withInternetCheck
} from "../components/utilis";
import {
  useCallback,
  useEffect,
  useState
} from "react";
import {
  GoogleAuthProvider,
  getAuth,
  sendSignInLinkToEmail,
  signInWithPopup
} from "firebase/auth";
import { initializeApp } from "firebase/app";
import {
  fbLogEvent,
  firebaseConfig
} from "../components/firebase";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
  redirect
} from "@remix-run/node";
import { login_cookie } from "../cookies.server";
import Loading from "../components/loader";

export const meta: MetaFunction = ({ }) => {
  const metaTitle = 'Login | Access Your Account';
  const metaDescription = 'Securely log in to access your account and manage your content, profile, and submissions. Enter your credentials to continue.';

  return [
    { title: `${websiteName}: ${metaTitle}` },
    { name: "description", content: metaDescription },
    { name: "image", content: '/images/aaoNxt-logo.png' },

    { property: "og:title", content: `${websiteName}: ${metaTitle}` },
    { property: "og:type", content: "business.business" },
    { property: "og:description", content: metaDescription },
    {
      property: "og:image",
      content: '/images/aaoNxt-logo.png',
    },

    { property: "twitter:title", content: `${websiteName}: ${metaTitle}` },
    { property: "twitter:type", content: "business.business" },
    {
      property: "twitter:description",
      content: metaDescription,
    },
    {
      property: "twitter:image",
      content: '/images/aaoNxt-logo.png',
    },

    {
      property: "linkedin:title",
      content: `${websiteName}: ${metaTitle}`,
    },
    { property: "linkedin:type", content: "business.business" },
    {
      property: "linkedin:description",
      content: metaDescription,
    },
    {
      property: "linkedin:image",
      content: '/images/aaoNxt-logo.png',
    },

  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const cookieHeader = request.headers.get('Cookie');
  const cookie = await login_cookie.parse(cookieHeader);

  const { APP_ENV } = process.env
  let apiUrl

  if (APP_ENV === "staging") {
    apiUrl = "https://stage-api.aaonxt.com";
  } else {
    apiUrl = "https://aao-api.ibee.ai";
  }

  if (cookie !== null) {

    redirect("/")
  } else {

    return {
      cookie: cookie?.accessToken ? cookie.accesToken : null,
      apiUrl: apiUrl
    };
  }

  return null
}

export async function action({ request }: ActionFunctionArgs) {
  const formInputValues = await request.clone().formData();
  const formData = Object.fromEntries(formInputValues);

  if (formData.action === 'accessToken') {
    const body = {
      accessToken: formData.accessToken,
      isAdmin: formData.isAdmin,
      userId: formData.userId
    };

    return redirect("/", {
      headers: {
        'Set-Cookie': await login_cookie.serialize(body, {
          expires: getExpiryDate(),
        }),
      },
    })
  }

  return null

}

const Login = () => {
  const loaderData = useLoaderData();
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const [isUserOffline, setIsUserOffline] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isEnteredMail, setIsEnteredMail] = useState("");
  const [isUserSubmittedMail, setIsUserSubmittedMail] = useState(false);

  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const googleProvider = new GoogleAuthProvider();

  useEffect(() => {
    if (loaderData?.cookie !== null) {
      navigate("/")
    }
  }, [loaderData])

  //  Login with Email 
  const submitEmail = useCallback(
    (e: { preventDefault: () => void; target: { value: any }[] }) => {
      e.preventDefault();

      const email = e.target[0].value;
      const emailRegEx =
        /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

      if (emailRegEx.test(email)) {

        const currentUrl =
          window.location.protocol +
          '//' +
          window.location.host +
          `/loginRedirect`;

        const actionCodeSettings = {
          url: currentUrl,
          handleCodeInApp: true,
        };

        sendSignInLinkToEmail(auth, email, actionCodeSettings)
          .then(() => {

            setEmailSent(true);
            window.localStorage.setItem('emailForSignIn', email);
          })
          .catch((error) => {
            return error
          });
      }
    },
    [auth]
  );

  const offlineNotification = () => {
    setIsUserOffline(true);
  };

  const submitEmailInternetCheck = (e: any) => {
    setIsUserSubmittedMail(true);
    e.preventDefault();
    withInternetCheck(navigator, submitEmail, e, offlineNotification);
  };

  const GetUserProfile = (access_token) => {

    const formData = new FormData();
    formData.append('access_token', access_token);

    fetch(`${loaderData?.apiUrl}/oauth2/creator`, {
      method: 'POST',
      body: formData
    }).then((res) => {

      if (res.status === 200) {
        return res.json()
      } else {
        return res.text().then(text => { throw (text) })
      }
    }).then((res) => {
      localStorage.setItem('token', res.access_token);
      localStorage.setItem('user_id', res.id);

      const isAdmin = res?.admin ? true : false // is admin or not
      const id = isAdmin ? res.admin_id : res.creator_id // creator id or user id

      fetcher.submit(
        {
          accessToken: res.access_token,
          action: "accessToken",
          isAdmin: isAdmin,
          userId: id
        },
        {
          method: 'POST',
        }
      );

    }).catch((err) => {
      return err
    })
  }

  //  Google Login
  const googleLogin = (e) => {
    e.preventDefault();

    //  Log FirebaseEvent
    fbLogEvent('gmail_login_begin', {
      method: "google",
      type: "google",
    }, localStorage);
    fbLogEvent('LoginBegin', {
      viaGmail: "NoError"
    }, localStorage);

    signInWithPopup(auth, googleProvider).then((result) => {
      const user = result.user;
      window.localStorage.setItem('emailForSignIn', result?._tokenResponse?.email);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = result.user.accessToken;
      GetUserProfile(accessToken);

      //  Log FirebaseEvent
      fbLogEvent('gmail_login_success', {
        method: "google",
        type: "google",
      }, localStorage);
      fbLogEvent('LoginSuccess', {
        LoginSuccessVia: "Gmail"
      }, localStorage)
      fbLogEvent('LandedOnManageProfile', {
        LandedVia: "Gmail"
      }, localStorage)

    }).catch((error) => {

      if (error.code === 'auth/account-exists-with-different-credential') {

        return null
      }

      //  Log FirebaseEvent
      fbLogEvent('gmail_login_fail', {
        method: "google",
        type: "google",
      }, localStorage);

    })
  }

  const getUserEnteredMail = (e) => {
    setIsEnteredMail(e.target.value);
  }

  if (fetcher?.state !== "idle") {

    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Loading />
      </div>
    );
  } else if (loaderData?.cookie === null) {

    return (
      <>
        <Header isActiveUser={false} />
        <div className="w-full h-full md:absolute md:left-0 md:top-0 md:flex md:items-center bg-black overflow-auto">
          <div className="w-full h-full fixed left-0 top-0 z-[2]">
            <img src="/images/partners-login-bg-image.png" alt="" className="w-full object-cover relative z-10" />
          </div>

          {/* login container */}
          {emailSent && !isUserOffline ? (
            <div className="w-full flex flex-col justify-center items-center h-[300px]">
              <p className='text-white text-[24px]'>
                Login link is sent to your email, kindly check and click on
                login link.
              </p>
            </div>
          ) : !emailSent && !isUserOffline ? (
            <div className="relative w-[90%] max-w-[381px] rounded-[5px] mx-auto my-8 z-30 shadow-lg bg-black bg-opacity-50 backdrop-blur-sm p-[20px] overflow-auto">
              <h1 className="text-white text-[24px] mb-[54px]">Creators Login</h1>
              <div className="w-full relative flex items-center justify-center">
                <div className="w-full rounded-[5px] text-center flex flex-col items-center">

                  <form onSubmit={(e) => submitEmailInternetCheck(e)}>
                    <input type="email" placeholder="Enter your mail" className="rounded-[5px] w-[341px] h-[63px] mb-[20px] p-2 bg-black text-white" onChange={(e) => getUserEnteredMail(e)} />
                    <button className={`text-white rounded-[5px] bg-primary w-[341px] h-[63px] text-[16px] ${isEnteredMail === "" || isUserSubmittedMail ? "opacity-20" : ""}`} type="submit" disabled={isEnteredMail === "" || isUserSubmittedMail}>
                      Continue
                    </button>
                  </form>

                  <div className="py-[28px] text-white relative flex items-center gap-8"><div className="border-b border-white opacity-[20%] w-[130px]"></div>or<div className="border-b border-white opacity-[20%] w-[130px]"></div></div>
                  <button className="text-dark font-bold bg-white w-[341px] h-[63px] text-[16px] flex items-center justify-center gap-x-2" onClick={(e) => googleLogin(e)} type="button">
                    <img src="images/google-icon.png" alt="google" />
                    Continue with Google
                  </button>
                </div>
              </div>
            </div>
          ) : ""}

          {isUserOffline ? (
            <div className="w-full mb-3 text-danger text-white flex justify-center items-center h-[100vh] text-[24px]">
              Offline: Please verify your internet connection and try again.
            </div>
          ) : (
            ''
          )}
        </div>
      </>
    );
  }
}

export default Login
